import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useUIStore } from '@/stores/ui';
import { MdMenuOpen } from 'react-icons/md';

import { ScrollableText } from './ScrollableText';
import { ScrollableChildText } from './ScrollableChildText';

import { HelpGuidesDrawer } from '@/components/help/HelpGuidesDrawer';
import { LegalDocumentsDrawer } from '@/components/legal/LegalDocumentsDrawer';
import { ModuleSelector } from './ModuleSelector';
import { SidebarAccountMenu } from './SidebarAccountMenu';
import { usePayloadMenu } from './usePayloadMenu';
interface SidebarProps {
  className?: string;
}

export const Sidebar = ({ className = '' }: SidebarProps) => {
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();

  const [showLabels, setShowLabels] = useState(!sidebarCollapsed);

  const getInitialOpenMenus = (
    menuItems: { key: string; path?: string; children?: { path?: string }[] }[],
  ) => {
    const openMenus: Record<string, boolean> = {};
    menuItems.forEach((item) => {
      if (item.children && item.children.length > 0) {
        if (item.path && location.pathname.startsWith(item.path)) {
          openMenus[item.key] = true;
        } else if (
          item.children.some(
            (child) => child.path && location.pathname.startsWith(child.path),
          )
        ) {
          openMenus[item.key] = true;
        } else {
          openMenus[item.key] = false;
        }
      }
    });
    return openMenus;
  };

  const payloadMenuItems = usePayloadMenu();

  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setOpenMenus(getInitialOpenMenus(payloadMenuItems));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, payloadMenuItems]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (!sidebarCollapsed) {
      timeout = setTimeout(() => setShowLabels(true), 280);
    } else {
      setShowLabels(false);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [sidebarCollapsed]);

  return (
    <motion.aside
      initial={false}
      animate={{
        maxWidth: sidebarCollapsed ? 64 : 256,
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{ overflow: 'visible', width: '100%' }}
      className={`relative z-20 bg-sidebar text-sidebar-foreground shadow-sidebar border-r border-sidebar-border flex flex-col h-screen transition-all duration-300 ${className}`}
    >
      <div
        className={`relative flex h-16 items-center gap-2 px-2 ${
          sidebarCollapsed ? 'justify-center' : 'justify-between'
        }`}
      >
        {!sidebarCollapsed && (
          <div className="min-w-0 flex-1 pr-2">
            <ModuleSelector collapsed={false} />
          </div>
        )}

        {sidebarCollapsed ? (
          <>
            <button
              onClick={toggleSidebar}
              className="absolute right-[-13px] top-1/2 z-[1000] -translate-y-1/2 rounded-lg bg-primary p-1 transition-colors dark:hover:bg-accent"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key="collapsed-icon"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                >
                  <motion.div
                    whileHover={{ x: [0, 3, 0] }}
                    transition={{
                      duration: 0.9,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </button>
            <ModuleSelector collapsed={true} />
          </>
        ) : (
          <button
            onClick={toggleSidebar}
            className="absolute right-[-13px] top-1/2 z-[1000] -translate-y-1/2 rounded-lg bg-primary p-1 transition-colors dark:hover:bg-accent"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <MdMenuOpen className="h-5 w-5" />
            </motion.div>
          </button>
        )}
      </div>

      <nav
        className={`flex-1 space-y-2 overflow-y-auto overflow-x-hidden sidebar-scroll transition-[padding] duration-300 ${
          sidebarCollapsed ? 'p-2' : 'p-4'
        }`}
      >
        {payloadMenuItems.map((item) => {
          // Menus expansíveis
          if (item.children && item.children.length > 0) {
            const isOpen = openMenus[item.key] || false;
            const isAnyChildActive = item.children.some(
              (child) => location.pathname === child.path,
            );
            return (
              <div key={item.key} className="space-y-1">
                <button
                  type="button"
                  onClick={() => {
                    if (sidebarCollapsed) toggleSidebar();
                    setOpenMenus((prev) => ({ ...prev, [item.key]: !isOpen }));
                  }}
                  className={`flex items-center w-full rounded-lg transition-all focus:outline-none
                    ${sidebarCollapsed ? 'justify-center px-3 py-3' : 'px-3 py-2 gap-3'}
                    ${
                      isAnyChildActive
                        ? 'bg-accent-foreground/30 text-white shadow-md'
                        : 'hover:bg-accent-foreground/20 text-sidebar-foreground'
                    }
                  `}
                  aria-expanded={isOpen}
                >
                  <div
                    className={`flex items-center flex-1 min-w-0 ${sidebarCollapsed ? '' : 'gap-3'}`}
                  >
                    {item.icon && (
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                    )}
                    {showLabels && !sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        className="font-medium text-left transition-all duration-300 truncate"
                      >
                        <ScrollableText text={item.name} />
                      </motion.span>
                    )}
                  </div>
                  {!sidebarCollapsed && (
                    <motion.span
                      initial={{ rotate: 0 }}
                      animate={{ rotate: isOpen ? 90 : 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 320,
                        damping: 25,
                      }}
                      className="flex-shrink-0"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </motion.span>
                  )}
                </button>
                {isOpen && !sidebarCollapsed && (
                  <motion.div
                    initial={{ height: 0, opacity: 0, y: 16 }}
                    animate={{ height: 'auto', opacity: 1, y: 0 }}
                    transition={{
                      type: 'spring',
                      stiffness: 140,
                      damping: 20,
                    }}
                    className="ml-5 space-y-1 border-l-2 border-sidebar-items dark:border-white overflow-hidden"
                  >
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const isActive = location.pathname === child.path;
                      return (
                        <Link
                          key={child.key}
                          to={child.path!}
                          className={`flex items-center rounded-lg transition-all space-x-3 px-4 py-2
                            ${isActive ? 'bg-accent-foreground/30 text-white font-semibold ml-2' : 'hover:bg-accent-foreground/20 text-sidebar-foreground'}
                          `}
                        >
                          <ScrollableChildText text={child.name} />
                        </Link>
                      );
                    })}
                  </motion.div>
                )}
              </div>
            );
          }

          // Menus simples
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.key}
              to={item.path!}
              className={`
                flex items-center rounded-lg transition-all
                ${sidebarCollapsed ? 'space-x-3 px-3 py-3' : 'space-x-3 px-3 py-3'}
                ${
                  isActive
                    ? 'bg-accent-foreground/30 text-white shadow-md'
                    : 'hover:bg-accent-foreground/20 text-sidebar-foreground'
                }
              `}
            >
              {Icon && <Icon className="w-5 h-5 flex-shrink-0 mr-1" />}
              {showLabels && !sidebarCollapsed && (
                <motion.span
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className="font-medium transition-all duration-300"
                >
                  <ScrollableChildText
                    text={item.name}
                    className="text-sidebar-foreground"
                  />
                </motion.span>
              )}
            </Link>
          );
        })}
      </nav>

      <div
        className={`transition-[padding] duration-300 ${
          sidebarCollapsed ? 'p-2' : 'p-2 pb-4'
        }`}
      >
        <SidebarAccountMenu
          sidebarCollapsed={sidebarCollapsed}
          showLabels={showLabels}
        />
      </div>

      <HelpGuidesDrawer />
      <LegalDocumentsDrawer />
    </motion.aside>
  );
};
